import { equals } from 'ramda';
import { FC, useEffect, useState, Fragment } from 'react';
import { useSelector } from 'react-redux';

import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from '~features/page/pageUtils';
import { getUserTimeFormat } from '~features/users/usersUtils';
import { useSubscription } from '~hooks/useSubscription';

import { LiveTrafficPacket } from './LiveTrafficPacket';

import { PageLoader } from '~components';
import { LiveTrafficPacket as ILiveTrafficPacket } from '~models';
import { boundActions, selectors } from '~store';

interface LiveTrafficContentProps {
  channel: string;
  liveTrafficData?: ILiveTrafficPacket[];
  limit?: boolean;
  isLive?: boolean;
  isExpanded?: boolean;
}

export const LiveTrafficContent: FC<LiveTrafficContentProps> = ({
  channel,
  liveTrafficData,
  limit,
  isLive,
  isExpanded,
}) => {
  const events = useSelector(selectors.liveTraffic.getLiveTraffic, equals);
  const recentItems = (events ?? [])
    .concat(liveTrafficData ?? [])
    .sort((a: ILiveTrafficPacket, b: ILiveTrafficPacket) => (a.createdAt > b.createdAt && -1) || 1);
  const liveTrafficEvents = liveTrafficData?.length ? recentItems : events;
  const isLoading = useSelector(selectors.liveTraffic.getLiveTrafficLoader, equals);
  const account = useSelector(selectors.profile.getAccount, equals);
  const timeFormat = getUserTimeFormat(account);
  const [liveTraffic, setLiveTraffic] = useState<ILiveTrafficPacket[]>([]);

  useSubscription<ILiveTrafficPacket[]>(channel, msg => {
    boundActions.liveTraffic.addLiveTraffic(msg.data);
  });

  useEffect(() => {
    setLocalStorageItem('liveTraffic', JSON.stringify([]));

    return () => {
      boundActions.liveTraffic.reset();
      removeLocalStorageItem('liveTraffic');
    };
  }, []);

  useEffect(() => {
    if (liveTrafficData?.length) {
      setLocalStorageItem('liveTraffic', JSON.stringify(liveTrafficData));
      setLiveTraffic(liveTrafficData);
    }
  }, [liveTrafficData]);

  useEffect(() => {
    if (isLive) {
      const savedEvents = getLocalStorageItem('liveTraffic');
      setLiveTraffic(limit ? savedEvents?.slice(0, 500) : savedEvents);
    }
  }, [isLive]);

  useEffect(() => {
    if (isLive) {
      setLiveTraffic(liveTrafficEvents);
    }
    setLocalStorageItem('liveTraffic', JSON.stringify(liveTrafficEvents));
  }, [events, isLive]);

  if (isLoading) {
    return <PageLoader fullscreen />;
  }

  return (
    <Fragment>
      {liveTraffic.map((event, i: number) => (
        <LiveTrafficPacket
          packet={event}
          key={event?.uniqueKey}
          eventKey={i}
          timeFormat={timeFormat}
          limitInfo={limit}
          isExpanded={isExpanded}
        />
      ))}
    </Fragment>
  );
};
